import {
  countOf,
  fmtMoney,
  type GbFinalChoice,
  type GbSeatView,
  type GoldenBallsViewerState
} from './types.js';

// ---------------------------------------------------------------------------
// Split or Steal (and STING) — the DECISION
// ---------------------------------------------------------------------------
// Two beats live here. FIRST the pre-final talk: the jackpot is up, the
// identities have arrived, and there is deliberately no commit button on
// screen — the last conversation happens before anyone can end it. THEN
// the choice, privately.
//
// The REVEAL is not here: the engine resolves the final and moves to
// 'ended' in one transition, so this component can never see a result.
// It lives in FinalCeremony.tsx, on the ended phase.
//
// The UI states each ball's rule plainly and stops there. It does not
// coach: in three-ball mode, announcing an intention to steal invites a
// sting, and players are meant to discover that themselves.

// Each button wears its BALL's colours, so what you press and what
// opens are the same object: SPLIT black on gold, STEAL white on red,
// STING gold on black. `blurb` needs its own tone because the rule text
// sits on a light ground for SPLIT, a red one for STEAL and black for
// STING.
const BALL_COPY: Record<
  GbFinalChoice,
  { title: string; blurb: string; tone: string; blurbTone: string }
> = {
  split: {
    title: 'Split',
    blurb: 'Half each if they split too. Nothing if they steal.',
    tone: 'border-ec-brass bg-ec-brass text-black',
    blurbTone: 'text-black/75'
  },
  steal: {
    title: 'Steal',
    blurb: 'Everything if they split. Nothing if they steal.',
    tone: 'border-[#e02a1f] bg-[#e02a1f] text-white',
    blurbTone: 'text-white/80'
  },
  sting: {
    title: 'Sting',
    blurb: 'You are accusing them of stealing.',
    tone: 'border-ec-brass/70 bg-black text-ec-brass',
    blurbTone: 'text-ec-bone'
  }
};

export function FinalRound({
  view,
  seats,
  yourSeatId,
  busy,
  onChoose
}: {
  view: GoldenBallsViewerState;
  seats: GbSeatView[];
  yourSeatId: string | null;
  busy: boolean;
  onChoose: (choice: GbFinalChoice) => void;
}) {
  const final = view.final;
  if (!final) return null;

  const talking = view.phase === 'pre_final';
  const finalists = seats.filter((seat) => seat.alive);
  const opponent = finalists.find((seat) => seat.seatId !== yourSeatId);
  const choices: GbFinalChoice[] =
    view.config.ballMode === 'threeBall' ? ['split', 'steal', 'sting'] : ['split', 'steal'];
  const stingReward = Math.round(view.config.stingReward * 100);
  const locked = final.yourChoice !== null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="shrink-0 rounded-lg border border-ec-brass/50 bg-ec-brass/5 p-3 text-center">
        <p className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          The jackpot
        </p>
        <p className="font-display text-3xl font-bold tabular-nums text-ec-brass">
          {fmtMoney(final.jackpot)}
        </p>
        <p className="font-serif text-[0.65rem] text-ec-bone-dim">Play money — no real value.</p>
      </div>

      {/* lateReveal: who you're actually facing. */}
      {Object.keys(view.identities).length > 0 && opponent && (
        <div className="shrink-0 rounded-lg border border-ec-line bg-ec-panel p-2 text-center">
          <p className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
            Across the table
          </p>
          <p className="font-display text-sm text-ec-face">
            {opponent.label}
            {view.identities[opponent.seatId] && (
              <span className="ml-1 text-ec-bone-dim">· {view.identities[opponent.seatId]}</span>
            )}
          </p>
          <p className="font-serif text-xs text-ec-bone">
            Verified this game:{' '}
            <span className="text-ec-go">{opponent.honesty.verifiedTrue} true</span>,{' '}
            <span className="text-ec-oxblood-2">{countOf(opponent.honesty.verifiedLied, 'lie')}</span>
          </p>
        </div>
      )}

      {/* Commitment status — flags only, never the choice itself. Not
          shown during the talk, where nobody can have committed. */}
      {!talking && (
        <div className="flex shrink-0 flex-wrap justify-center gap-2">
          {finalists.map((seat) => (
            <span
              key={seat.seatId}
              className={`rounded-full border px-2 py-0.5 font-display text-[0.6rem] uppercase tracking-wide ${
                final.committedSeats[seat.seatId]
                  ? 'border-ec-go/60 text-ec-go'
                  : 'border-ec-line text-ec-bone-dim'
              }`}
            >
              {seat.label} {final.committedSeats[seat.seatId] ? 'locked in' : 'deciding…'}
            </span>
          ))}
        </div>
      )}

      {talking ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-center">
          <p className="font-display text-lg uppercase tracking-[0.25em] text-ec-brass">
            Last words
          </p>
          <p className="max-w-sm font-serif text-sm text-ec-bone">
            One conversation before the balls come out. Make your case, make a
            promise, or say nothing at all — {opponent?.label ?? 'the other finalist'} is
            listening either way.
          </p>
          <p className="max-w-sm font-serif text-xs text-ec-bone-dim">
            Talk in the <strong className="text-ec-face">Talk</strong> tab. Nothing is
            committed yet; the balls appear when the clock runs down or you both
            say you are ready.
          </p>
        </div>
      ) : (
        /* THE CHOICE. shrink-0, so all three balls are on screen at
           every viewport. As a flex-1 column underneath a seat-panel
           table this overflowed a short container that had no scroll,
           and silently clipped STEAL and STING — the owner could only
           pick SPLIT, which is a broken game, not a layout nit. The
           seat panels are gone from this phase now too. */
        <div className="flex shrink-0 flex-col gap-2">
          {choices.map((choice) => (
            <button
              key={choice}
              type="button"
              disabled={busy || locked}
              onClick={() => onChoose(choice)}
              className={[
                'flex min-h-[3.25rem] flex-col items-start justify-center rounded-lg border px-3 py-1.5 text-left transition',
                BALL_COPY[choice].tone,
                final.yourChoice === choice ? 'ring-2 ring-ec-brass' : '',
                'disabled:opacity-50'
              ].join(' ')}
            >
              <span className="font-display text-sm font-semibold uppercase tracking-wide">
                {BALL_COPY[choice].title}
              </span>
              <span
                className={`font-serif text-[0.7rem] leading-tight ${BALL_COPY[choice].blurbTone}`}
              >
                {choice === 'sting'
                  ? `${BALL_COPY[choice].blurb} Right: you take ${stingReward}% and they get nothing. Wrong: you get nothing and they keep their half.`
                  : BALL_COPY[choice].blurb}
              </span>
            </button>
          ))}
          {locked && (
            <p className="text-center font-display text-xs uppercase tracking-wide text-ec-bone-dim">
              Locked in. Waiting for {opponent?.label ?? 'the other finalist'}…
            </p>
          )}
        </div>
      )}
    </div>
  );
}
